import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

// Only these section-owned markers opt in. Native cart and complementary
// recommendations keep their existing rendering owners.
const markerSelector = '[data-bf-cart-recommendations]';
const pendingMutations = new Set();
const seenMutations = new WeakSet();
const activeRenders = new Map();
let listenerController;
let revision = 0;
let lifecycle = 0;
let needsRefresh = false;

function sectionIds() {
  return [...new Set([...document.querySelectorAll(markerSelector)].map((marker) => marker.dataset.bfCartRecommendations))]
    .filter((id) => id && document.getElementById(`shopify-section-${id}`));
}

function cancelRenders() {
  for (const id of activeRenders.keys()) sectionRenderer.abortRender(id);
  activeRenders.clear();
}

/** Preserve keyboard focus if a chosen card disappears, without taking it from a drawer. */
function restoreFocus(previousFocus, section) {
  if (!previousFocus || (previousFocus.isConnected && !section.hidden)) return;
  if (document.activeElement !== document.body && document.activeElement !== previousFocus) return;
  const heading = (!section.hidden && section.querySelector('h2, h3')) || document.querySelector('main h1');
  if (!(heading instanceof HTMLElement)) return;
  if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
  heading.focus({ preventScroll: true });
}

/** Refresh only suggestion sections after all known cart mutations have settled. */
async function refreshRecommendations(currentRevision) {
  await Promise.all(sectionIds().map(async (id) => {
    const section = document.getElementById(`shopify-section-${id}`);
    if (!(section instanceof HTMLElement)) return;
    const previousFocus = section.contains(document.activeElement) ? document.activeElement : null;
    const isCurrent = () => currentRevision === revision && !listenerController.signal.aborted;
    activeRenders.set(id, currentRevision);
    section.setAttribute('aria-busy', 'true');

    try {
      await sectionRenderer.renderSection(id, {
        cache: false,
        mode: 'full',
        injectStylesheet: true,
        shouldRender: isCurrent,
      });
      if (!isCurrent()) return;
      const updatedSection = document.getElementById(`shopify-section-${id}`);
      if (!(updatedSection instanceof HTMLElement)) return;
      updatedSection.hidden = false;
      restoreFocus(previousFocus, updatedSection);
    } catch (error) {
      if (!isCurrent() || error?.name === 'AbortError') return;
      // A failed refresh must not keep an already chosen or unavailable item
      // on offer. The hidden marker allows recovery on the next cart change.
      const staleSection = document.getElementById(`shopify-section-${id}`);
      if (staleSection instanceof HTMLElement) {
        staleSection.hidden = true;
        restoreFocus(previousFocus, staleSection);
      }
    } finally {
      if (activeRenders.get(id) === currentRevision) activeRenders.delete(id);
      if (isCurrent()) document.getElementById(`shopify-section-${id}`)?.removeAttribute('aria-busy');
    }
  }));
}

/** @param {Event & { promise?: Promise<{ detail?: { didError?: boolean } }> }} event */
function onCartUpdate(event) {
  if (!sectionIds().length || !event.promise || seenMutations.has(event.promise)) return;
  seenMutations.add(event.promise);
  revision += 1;
  if (activeRenders.size) needsRefresh = true;
  cancelRenders();
  const currentLifecycle = lifecycle;
  const mutation = event.promise;
  pendingMutations.add(mutation);

  mutation.then((result) => {
    if (currentLifecycle === lifecycle && !result?.detail?.didError) needsRefresh = true;
  }).catch(() => {
    // The native form/cart owns mutation errors and their announcement.
  }).finally(() => {
    if (currentLifecycle !== lifecycle) return;
    pendingMutations.delete(mutation);
    if (pendingMutations.size || !needsRefresh) return;
    needsRefresh = false;
    void refreshRecommendations(revision);
  });
}

function listen() {
  listenerController?.abort();
  listenerController = new AbortController();
  document.addEventListener(StandardEvents.cartLinesUpdate, onCartUpdate, { signal: listenerController.signal });
}

listen();
window.addEventListener('pagehide', () => {
  listenerController.abort();
  lifecycle += 1;
  revision += 1;
  needsRefresh = false;
  pendingMutations.clear();
  cancelRenders();
  for (const id of sectionIds()) document.getElementById(`shopify-section-${id}`)?.removeAttribute('aria-busy');
});
window.addEventListener('pageshow', (event) => {
  listen();
  if (event.persisted) void refreshRecommendations(++revision);
});
